import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import ErrorMessage from '@/components/common/ErrorMessage';
import FieldLayout from '@/components/Layout/FieldLayout';
import Button from '@/components/ui/Button';
import Label from '@/components/ui/Label';
import SearchSelectBox, {
  ISelectOption,
} from '@/components/ui/SearchSelectBox';
import { IAssetResponse } from '@/interfaces/assets.model';
import { getFilterUrl, showToastNotification } from '@/utility/helper';
import { useRef, useState } from 'react';
import { Modal, ModalBody, Spinner } from 'reactstrap';

interface IAllocationApprovalModalProps {
  isOpen: boolean;
  toggle: () => void;
  submitHandler: (assetId: string) => void;
  isLoading: boolean;
  assetCategoryId: string | null;
}

const AllocationApprovalModal = ({
  isOpen,
  toggle,
  submitHandler,
  isLoading,
  assetCategoryId,
}: IAllocationApprovalModalProps) => {
  const [selectedAssetId, setSelectedAssetId] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isAssetLoading, setIsAssetLoading] = useState(false);
  const [assetOptions, setAssetOptions] = useState<ISelectOption[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState('');

  const loadingRef = useRef(false);
  const pageRef = useRef(1);

  const getAssets = async (
    inputValue?: string,
    currentPage: number = 1,
  ): Promise<ISelectOption[]> => {
    if (loadingRef.current && currentPage !== 1) return [];

    try {
      loadingRef.current = true;
      setIsAssetLoading(true);

      const filter = {
        search: inputValue || '',
        assetCategoryId: assetCategoryId || '',
        status: '1',
        pageSize: 10,
        pageNumber: currentPage,
      };

      const API = `${API_URL}${API_ENDPOINT?.assetList}?${getFilterUrl(filter)}`;
      const response: IAssetResponse = await callApi(API, {
        method: 'GET',
      });

      if (response.status) {
        const newOptions = response.data?.asset.map((item) => ({
          value: item.id,
          label: item.name,
        }));

        const { pageInfo } = response.data;
        const moreAvailable = pageInfo.currentPage < pageInfo.totalPages;
        setHasMore(moreAvailable);

        if (currentPage === 1) {
          setAssetOptions(newOptions);
          pageRef.current = 1;
        } else {
          setAssetOptions((prev) => [...prev, ...newOptions]);
          pageRef.current = currentPage;
        }
        return newOptions;
      }

      return [];
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
      return [];
    } finally {
      loadingRef.current = false;
      setIsAssetLoading(false);
    }
  };

  const handleMenuScrollToBottom = () => {
    if (hasMore && !loadingRef.current) {
      const nextPage = pageRef.current + 1;
      getAssets(search, nextPage);
    }
  };

  const handleSearchChange = (inputValue: string) => {
    setSearch(inputValue);
    pageRef.current = 1;
    return getAssets(inputValue, 1);
  };

  const handleSubmit = () => {
    if (!selectedAssetId) {
      setError('Please select an asset');
      return;
    }
    submitHandler(selectedAssetId);
  };

  const handleToggle = () => {
    setError('');
    setSelectedAssetId('');
    pageRef.current = 1;
    setAssetOptions([]);
    setHasMore(true);
    setSearch('');
    loadingRef.current = false;
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleToggle} centered>
      <div className="modal-header p-4">
        <h1 className="modal-title fs-3 rajdhani-semibold">
          Approve Allocation
        </h1>
        <button type="button" className="btn-close" onClick={handleToggle} />
      </div>
      <ModalBody className="p-4">
        <div className="d-flex flex-column gap-3">
          <FieldLayout>
            <Label required={true} id="assetSelect">
              Select Asset
            </Label>
            <SearchSelectBox
              value={selectedAssetId}
              title="Select Asset"
              onChange={(option) => {
                setSelectedAssetId(option?.value || '');
                setError('');
              }}
              disable={isLoading}
              loading={isAssetLoading}
              isAsync
              fetchOptions={handleSearchChange}
              options={assetOptions}
              defaultFetch
              isUsingFilter
              onMenuScrollToBottom={handleMenuScrollToBottom}
            />
            <ErrorMessage message={error} />
          </FieldLayout>

          <div className="row m-0 w-100 mt-3">
            <div className="col-12 col-md-6 ps-0 pe-3">
              <button
                type="button"
                className="btn btn-secondary w-100"
                onClick={handleToggle}
              >
                Cancel
              </button>
            </div>
            <div className="col-12 col-md-6 pe-0 ps-3">
              <Button
                className="btn btn-success w-100"
                onClick={handleSubmit}
                disabled={isLoading || isAssetLoading}
                type="button"
              >
                {isLoading || isAssetLoading ? (
                  <Spinner size="sm" color="light" />
                ) : (
                  'Approve'
                )}
              </Button>
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default AllocationApprovalModal;
